/**
 * Fund WRITE endpoints — request schemas.
 *
 * Every fund write except DELETE goes through ONE RPC, `save_fund`
 * (migration 20261006210000, fund management P1): the header, the
 * installment schedule (replaced wholesale until an installment is invoiced)
 * and the award. The Hono handlers in
 * `apps/api/src/routes/accounting/funds.ts` validate the body here and map it
 * onto the RPC's arguments:
 *   - every header field below            → `p_fund` (a key that is present —
 *                                           null included — is written; an
 *                                           absent key leaves the column alone)
 *   - `installments`                      → `p_installments` (absent = leave
 *                                           the schedule alone)
 *   - `award_date`                        → `p_award_date` (present = award:
 *                                           one AR invoice per installment,
 *                                           numbered `<fund_code>/<sequence>`)
 * The SQL re-checks every rule (status transitions, locks, accounts, closed
 * periods) against the merged row; these schemas only reject malformed input.
 *
 * DELETE stays Pattern B (a plain RLS + token-gated delete). Responses:
 * `SaveFundResponse` in `fundsReads.ts`.
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
/** The `funds_fund_type_check` CHECK constraint's 4 allowed values. */
export const fundTypeSchema = z.enum([
    "unrestricted",
    "restricted",
    "temporarily_restricted",
    "permanently_restricted",
]);
/** `funds.status` CHECK values. `save_fund` enforces the allowed transitions. */
export const fundStatusSchema = z.enum([
    "pipeline",
    "awarded",
    "not_awarded",
    "cancelled",
    "closed",
]);
/** `funds.likelihood` CHECK values (pipeline funds). */
export const fundLikelihoodSchema = z.enum(["high", "medium", "low"]);
/**
 * `fund_installments.recognition_trigger` CHECK values in P1: `on_award`
 * posts the installment straight to income, `on_receipt` to the deferred
 * (liability) account.
 */
export const fundRecognitionTriggerSchema = z.enum(["on_award", "on_receipt"]);
/**
 * A fund code: at most 20 characters and no `/` — installment invoices are
 * numbered `<fund_code>/<sequence>`, so a slash in the code would make the
 * number ambiguous.
 */
export const fundCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[^/]+$/, { message: 'A fund code cannot contain "/"' });
/**
 * One installment of the schedule. Its `sequence` is its position in the
 * array (1-based) — the RPC numbers them, so the body carries none.
 */
export const fundInstallmentInputSchema = z
    .object({
    amount: z.number().positive().finite(),
    expected_date: calendarDateSchema.nullable().optional(),
    recognition_trigger: fundRecognitionTriggerSchema,
})
    .strict();
const optionalUuid = z.string().uuid().nullable().optional();
const optionalDate = calendarDateSchema.nullable().optional();
/** The header fields `save_fund` reads from `p_fund`. */
const fundHeaderFields = {
    fund_name: z.string().trim().min(1).max(255),
    fund_type: fundTypeSchema,
    restriction_description: z.string().nullable().optional(),
    project_id: optionalUuid,
    status: fundStatusSchema.optional(),
    funder_business_id: optionalUuid,
    reference_number: z.string().max(255).nullable().optional(),
    period_start: optionalDate,
    period_end: optionalDate,
    requested_amount: z.number().nonnegative().finite().nullable().optional(),
    likelihood: fundLikelihoodSchema.nullable().optional(),
    receivable_account_id: optionalUuid,
    deferred_account_id: optionalUuid,
    income_account_id: optionalUuid,
};
/** The non-header keys: the schedule and the award. */
const fundScheduleFields = {
    installments: z.array(fundInstallmentInputSchema).max(120).optional(),
    /** Present = award the fund on this (calendar) day. */
    award_date: calendarDateSchema.optional(),
};
/** End on or after start, when both are in the body (the RPC checks the merged row). */
const periodInOrder = (v) => !v.period_start || !v.period_end || v.period_end >= v.period_start;
const periodMessage = { message: "period_end must be on or after period_start", path: ["period_end"] };
/**
 * POST /orgs/:orgId/funds body. `organization_id` comes from the validated
 * `:orgId` route param (never the body); `fund_code` is optional — the
 * `auto_fund_code` trigger assigns the next one. No `is_active` (generated:
 * `status = 'awarded'`) and no `grant_id` (dropped in 20261006210000).
 */
export const saveFundSchema = z
    .object({
    ...fundHeaderFields,
    fund_code: fundCodeSchema.optional(),
    ...fundScheduleFields,
})
    .strict()
    .refine(periodInOrder, periodMessage);
/**
 * PATCH /orgs/:orgId/funds/:fundId body — any subset, at least one field.
 * `fund_code` is deliberately omitted: codes are immutable after creation
 * (`save_fund` refuses a change with FUND_CODE_INVALID too).
 */
export const updateFundSchema = z
    .object({
    ...fundHeaderFields,
    ...fundScheduleFields,
})
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field must be provided",
})
    .refine(periodInOrder, periodMessage);
/**
 * The semantic `error_code`s `save_fund` returns in its `{ success: false }`
 * envelope. The API answers each with `{ error: <code>, code: <code> }` (400)
 * so the web maps it to a translated message. `FUND_AWARD_FAILED` is not one
 * of them: it is a RAISE (the whole award rolls back) and arrives as a plain
 * message 400.
 */
export const FUND_ERROR_CODES = [
    "FUND_NOT_FOUND",
    "FUND_INVALID",
    "FUND_CODE_INVALID",
    "FUND_CODE_TAKEN",
    "FUND_NAME_TAKEN",
    "FUND_STATUS_INVALID",
    "FUND_FUNDER_INVALID",
    "FUND_PROJECT_INVALID",
    "FUND_INSTALLMENT_INVALID",
    "FUND_INSTALLMENTS_LOCKED",
    "FUND_NOT_AWARDABLE",
    "FUND_INSTALLMENTS_REQUIRED",
    "FUND_ACCOUNTS_REQUIRED",
    "FUND_ACCOUNT_INVALID",
    "FUND_AWARD_DATE_FUTURE",
    "FUND_PERIOD_CLOSED",
    "FUND_INVOICE_NUMBER_TAKEN",
];
//# sourceMappingURL=fundsWrites.js.map